import PropTypes from "prop-types";
import * as Yup from "yup";
import { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import { useFormik, Form, FormikProvider } from "formik";
import { Link as RouterLink } from "react-router-dom";
// material
import {
  Card,
  Alert,
  TextField,
  Stack,
  CardHeader,
  Box,
  Autocomplete,
  Typography,
  InputAdornment,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Link,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import useAuth from "../../../hooks/useAuth";
import { CheckPerm, NumberFormatCustom } from "../../../utils/apis";
import { fCurrency } from "../../../utils/formatNumber";
import { PATH_DASHBOARD } from "../../../routes/paths";
// ----------------------------------------------------------------------
ProductUpdate.propTypes = {
  myProfile: PropTypes.object,
  uid: PropTypes.string,
  fetch: PropTypes.func,
};
export default function ProductUpdate({ myProfile, uid, fetch }) {
  const { updateProduct, getCategories, getBranches, getCompanies, user } =
    useAuth();
  const { settings } = user;
  const isVatAllowed = parseInt(
    (settings &&
      Array.isArray(settings) &&
      settings.filter((setting) => setting.name === "allow_vat")[0].value) ||
      0,
    10,
  );
  const generalVat = parseFloat(
    (settings &&
      Array.isArray(settings) &&
      settings.filter((setting) => setting.name === "vat_value")[0].value) ||
      0,
    10,
  );
  const [CATEGORIES, setCATEGORIES] = useState([]);
  const [BRANCHES, setBRANCHES] = useState([]);
  const [COMPANIES, setCOMPANIES] = useState([]);
  const hasFullPermission = CheckPerm("update_products");
  const isEcomProduct = parseInt(myProfile?.isEcommerce || 0, 10) === 1;
  const hasEcomPermission =
    CheckPerm("update_products_ecommerce_details") && isEcomProduct;
  const canEdit = hasFullPermission || hasEcomPermission;
  const { enqueueSnackbar } = useSnackbar();

  const Schema = Yup.object().shape({
    name: Yup.string().required("Product name is required"),
    selling: Yup.number("Enter a valid selling price to proceed.").required(
      "Selling price is required",
    ),
    vat: Yup.number("Enter a valid VAT value to proceed")
      .max(100, "Your VAT cannot be more than 100%")
      .min(0, "Your VAT cannot be less than 0%"),
    branch: Yup.object().required("Branch is required!"),
  });

  const formik = useFormik({
    initialValues: {
      name: myProfile.name,
      number: myProfile.number,
      selling: myProfile.sellingPrice,
      wholesale: myProfile.wholesalePrice,
      category: {},
      company: "",
      cost: myProfile.costPrice,
      low_stock: myProfile.lowStock,
      branch: {},
      vat: myProfile.vat,
      attributes: JSON.parse(myProfile.attributes),
      isService: parseInt(myProfile.isService, 10),
      qtyPrice: { name: "", quantity: "", price: "" },
      qtyPrices: myProfile.qtyPrices ? JSON.parse(myProfile.qtyPrices) : [],
    },
    validationSchema: Schema,
    onSubmit: async (values, { setErrors, setSubmitting }) => {
      try {
        if (values.isService === 1) {
          const isEmpty = values.attributes.filter(
            (milestone) => milestone.name === "" || milestone.value === "",
          );
          if (isEmpty.length === 0) {
            // check if percentage is equal to 100.
            const totalPercentage = values.attributes.reduce(
              (total, current) => total + parseFloat(current.value),
              0,
            );
            if (totalPercentage > 100) {
              throw new Error(
                "The total completion percentage is greater than 100%. Please make sure your completion percentage equals 100% and try again.",
              );
            }
            if (totalPercentage < 100) {
              throw new Error(
                "The total completion percentage is less than 100%. Please make sure your completion percentage equals 100% and try again.",
              );
            }
          } else {
            throw new Error("Please fill all the milestone fields to proceed.");
          }
        } else if (values.cost === "" && hasFullPermission) {
          setErrors({ cost: "Enter a valid cost price to proceed." });
          return;
        } else if (values.low_stock === "") {
          setErrors({ low_stock: "Enter a valid limit to proceed." });
          return;
        }
        await updateProduct(values, uid);
        enqueueSnackbar(
          `${
            values.isService === 0 ? "Product" : "Service"
          }'s data has been updated!`,
          {
            variant: "success",
          },
        );
        setSubmitting(false);
        fetch();
      } catch (error) {
        setErrors({ afterSubmit: error.message });
        setSubmitting(false);
        window.scrollTo(0, 0);
      }
    },
  });

  const {
    errors,
    touched,
    handleSubmit,
    isSubmitting,
    getFieldProps,
    values,
    setFieldValue,
  } = formik;

  useEffect(() => {
    async function get() {
      const categories = await getCategories();
      const branches = await getBranches();
      const companies = await getCompanies("");
      setCATEGORIES(categories.filter((cat) => cat.name !== "All"));
      setBRANCHES([{ name: "All Branches", bid: "" }, ...branches]);
      setCOMPANIES(companies);
      const selectedCategory = categories.filter(
        (category) => category.cid === myProfile.category,
      );
      const selectedBranch = branches.filter(
        (branch) => branch.bid === myProfile.branch,
      );
      const selectedCompany = companies.filter(
        (company) => company.cid === myProfile.company,
      );
      setFieldValue(
        "category",
        selectedCategory.length > 0 ? selectedCategory[0] : {},
      );
      setFieldValue(
        "branch",
        selectedBranch.length > 0 ? selectedBranch[0] : {},
      );
      setFieldValue(
        "company",
        selectedCompany.length > 0 ? selectedCompany[0].name : "",
      );
    }
    get();
  }, [
    getCategories,
    getBranches,
    getCompanies,
    myProfile.branch,
    myProfile.category,
    myProfile.company,
    setFieldValue,
  ]);

  return (
    <Card
      variant="outlined"
      sx={{
        border: "0.4px solid",
        borderColor: "background.border",
        boxShadow:
          "4px 2px 124px rgba(46, 53, 58, 0.02), 0px 4px 124px rgba(46, 41, 78, 0.02)",
        borderRadius: "8px",
        px: 3,
        pb: 3,
      }}
    >
      <CardHeader
        title={`${values.isService === 1 ? "Service" : "Product"} Information`}
      />
      <Box sx={{ p: { md: 3, xs: 1 } }}>
        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Stack spacing={3}>
              {errors.afterSubmit && (
                <Alert severity="error">{errors.afterSubmit}</Alert>
              )}
              <TextField
                fullWidth
                label="Name"
                {...getFieldProps("name")}
                error={Boolean(touched.name && errors.name)}
                helperText={touched.name && errors.name}
                disabled={!canEdit}
              />
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                {values.isService === 0 && (
                  <TextField
                    fullWidth
                    label="Low Stock Limit"
                    {...getFieldProps("low_stock")}
                    disabled={!canEdit}
                    error={Boolean(touched.low_stock && errors.low_stock)}
                    helperText={touched.low_stock && errors.low_stock}
                  />
                )}

                <TextField
                  fullWidth
                  label="Product ID / Number"
                  {...getFieldProps("number")}
                  error={Boolean(touched.number && errors.number)}
                  disabled={!canEdit}
                  helperText={touched.number && errors.number}
                />
              </Stack>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                {(hasFullPermission && values.isService === 0 && (
                  <TextField
                    fullWidth
                    label="Cost Price"
                    {...getFieldProps("cost")}
                    disabled={!hasFullPermission}
                    error={Boolean(touched.cost && errors.cost)}
                    helperText={touched.cost && errors.cost}
                    InputProps={{
                      inputComponent: NumberFormatCustom,
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                )) ||
                  ""}
                <Stack sx={{ width: 1 }} spacing={0.5}>
                  <TextField
                    fullWidth
                    label={
                      values.isService === 0
                        ? "Selling Price (retail)"
                        : "Service Cost"
                    }
                    {...getFieldProps("selling")}
                    disabled={!canEdit}
                    error={Boolean(touched.selling && errors.selling)}
                    helperText={touched.selling && errors.selling}
                    InputProps={{
                      inputComponent: NumberFormatCustom,
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  {(hasFullPermission && values.selling && values.cost && (
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary" }}
                    >
                      Profit Margin:{" "}
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{ fontWeight: 600 }}
                      >
                        {fCurrency(
                          parseFloat(values.selling) - parseFloat(values.cost),
                        )}
                      </Typography>
                    </Typography>
                  )) ||
                    ""}
                </Stack>
              </Stack>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                {values.isService === 0 && (
                  <Stack sx={{ width: 1 }} spacing={0.5}>
                    <TextField
                      fullWidth
                      label="Selling Price (wholesale)"
                      {...getFieldProps("wholesale")}
                      disabled={!canEdit}
                      error={Boolean(touched.wholesale && errors.wholesale)}
                      helperText={touched.wholesale && errors.wholesale}
                      InputProps={{
                        inputComponent: NumberFormatCustom,
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                    {(hasFullPermission && values.wholesale && values.cost && (
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary" }}
                      >
                        Profit Margin:{" "}
                        <Typography
                          component="span"
                          variant="caption"
                          sx={{ fontWeight: 600 }}
                        >
                          {fCurrency(
                            parseFloat(values.wholesale) -
                              parseFloat(values.cost),
                          )}
                        </Typography>
                      </Typography>
                    )) ||
                      ""}
                  </Stack>
                )}
                <Stack sx={{ width: 1 }} spacing={0.5}>
                  <TextField
                    fullWidth
                    label="Value Added Tax (%)"
                    {...getFieldProps("vat")}
                    disabled={!canEdit || isVatAllowed === 0}
                    error={Boolean(touched.vat && errors.vat)}
                    helperText={touched.vat && errors.vat}
                    InputProps={{
                      endAdornment: "%",
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  {(canEdit && isVatAllowed === 1 && (
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary" }}
                    >
                      This value will override the general VAT value{" "}
                      <Typography
                        variant="caption"
                        component="span"
                        sx={{ color: "secondary.main", fontWeight: 600 }}
                      >
                        ({generalVat}%)
                      </Typography>{" "}
                      set under the Shop settings page.
                    </Typography>
                  )) ||
                    (canEdit && isVatAllowed === 0 && (
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary" }}
                      >
                        To use the VAT feature and set the value, please go
                        to&nbsp;
                        <Link
                          variant="caption"
                          component={RouterLink}
                          to={PATH_DASHBOARD.general.preferences}
                          sx={{ color: "secondary.main", fontWeight: 600 }}
                        >
                          the settings page
                        </Link>{" "}
                        to activate it.
                      </Typography>
                    )) ||
                    ""}
                </Stack>
              </Stack>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <Autocomplete
                  fullWidth
                  options={CATEGORIES}
                  {...getFieldProps("category")}
                  disabled={!canEdit}
                  name="category"
                  value={values.category}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setFieldValue("category", newValue);
                      const attributes = JSON.parse(newValue.attributes);
                      setFieldValue(
                        "attributes",
                        attributes.map((attr) => ({ name: attr, value: "" })),
                      );
                    }
                  }}
                  getOptionLabel={(option) => option.name || ""}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={
                        values.isService === 0
                          ? "Product Category"
                          : "Service Category"
                      }
                      margin="none"
                      error={Boolean(touched.category && errors.category)}
                      helperText={touched.category && errors.category}
                    />
                  )}
                />
                <Autocomplete
                  fullWidth
                  options={BRANCHES}
                  {...getFieldProps("branch")}
                  name="branch"
                  value={values.branch}
                  disabled={!canEdit}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setFieldValue("branch", newValue);
                    }
                  }}
                  getOptionLabel={(option) => option.name || ""}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Branch"
                      margin="none"
                      error={Boolean(touched.branch && errors.branch)}
                      helperText={touched.branch && errors.branch}
                    />
                  )}
                />
              </Stack>
              {values.isService === 0 && (
                <Autocomplete
                  fullWidth
                  freeSolo
                  options={COMPANIES.map((option) => option.name)}
                  {...getFieldProps("company")}
                  disabled={!canEdit}
                  name="company"
                  value={values.company}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setFieldValue("company", newValue);
                    }
                  }}
                  onInputChange={(event, newInputValue) => {
                    setFieldValue("company", newInputValue);
                  }}
                  getOptionLabel={(option) => option || ""}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Purchased From"
                      margin="none"
                      error={Boolean(touched.company && errors.company)}
                      helperText={touched.company && errors.company}
                    />
                  )}
                />
              )}
              {values.attributes.length > 0 && values.isService === 0 && (
                <Stack direction="column" spacing={2}>
                  <Typography sx={{ mt: 2 }} variant="h6">
                    Attributes
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    sx={{ color: "text.disabled" }}
                  >
                    Please enter appropriate value(s) for the attributes
                    associated with this product category.
                  </Typography>
                  {values.attributes.map((attribute, index) => (
                    <Stack
                      alignItems="center"
                      direction={{ xs: "column", md: "row" }}
                      key={index}
                      spacing={2}
                      sx={{ width: 1 }}
                    >
                      <Typography>{attribute.name}:</Typography>
                      <TextField
                        size="small"
                        name="attribute"
                        {...getFieldProps(`attributes[${index}].value`)}
                        disabled={!canEdit}
                        fullWidth
                        type="text"
                        margin="dense"
                        variant="outlined"
                        label={`${attribute.name} value`}
                      />
                    </Stack>
                  ))}
                </Stack>
              )}
              {values.isService === 1 && (
                <Stack direction="column" spacing={1}>
                  <Typography variant="subtitle2">Milestones:</Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Please specify the milestones and their completion
                    percentages for this service.
                  </Typography>
                  <Stack spacing={2} sx={{ pt: 2 }}>
                    {values.attributes.map((attribute, index) => (
                      <Stack
                        alignItems="center"
                        direction={{ xs: "column", md: "row" }}
                        key={index}
                        spacing={2}
                        sx={{ width: 1 }}
                      >
                        <TextField
                          size="small"
                          name="name"
                          {...getFieldProps(`attributes[${index}].name`)}
                          fullWidth
                          type="text"
                          margin="dense"
                          variant="outlined"
                          label="Milestone name/description"
                        />
                        <TextField
                          size="small"
                          name="value"
                          {...getFieldProps(`attributes[${index}].value`)}
                          sx={{
                            maxWidth: { md: 300, xs: 1 },
                            width: { md: "auto", xs: 1 },
                            "& label": { fontSize: 13 },
                          }}
                          type="number"
                          margin="dense"
                          variant="outlined"
                          label="Completion Percentage"
                          InputProps={{
                            endAdornment: <InputAdornment>%</InputAdornment>,
                          }}
                        />
                        <Button
                          variant="outlined"
                          size="small"
                          color="error"
                          onClick={() =>
                            setFieldValue(
                              "attributes",
                              values.attributes.filter((e, i) => i !== index),
                            )
                          }
                        >
                          Remove
                        </Button>
                      </Stack>
                    ))}
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{ width: "max-content" }}
                      onClick={() =>
                        setFieldValue("attributes", [
                          ...values.attributes,
                          { name: "", value: "" },
                        ])
                      }
                    >
                      Add Milestone
                    </Button>
                  </Stack>
                </Stack>
              )}
              {values.isService === 0 && (
                <Stack direction="column" spacing={0}>
                  <Typography sx={{ mt: 2 }} variant="h6">
                    Quantity-based Pricing (optional)
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    sx={{ color: "text.disabled" }}
                  >
                    You can specify the different selling prices for each type
                    of quantities you sell. For example, you may specify a price
                    for a carton or crate or a pack of a product.
                  </Typography>

                  <TableContainer sx={{ width: 1, maxWidth: 1, py: 4 }}>
                    <Table>
                      <TableHead
                        sx={{
                          borderBottom: (theme) =>
                            `solid 1px ${theme.palette.divider}`,
                          "& th": {
                            backgroundColor: "transparent",
                          },
                        }}
                      >
                        <TableRow>
                          <TableCell align="left">Name</TableCell>
                          <TableCell align="left" width={80}>
                            Qty
                          </TableCell>
                          <TableCell align="left">Selling Price</TableCell>
                          <TableCell align="left">Profit Margin</TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {values.qtyPrices.map((row, index) => (
                          <TableRow
                            key={index}
                            sx={{
                              borderBottom: (theme) =>
                                `solid 1px ${theme.palette.divider}`,
                            }}
                          >
                            <TableCell align="left">{row.name}</TableCell>
                            <TableCell align="left">{row.quantity}</TableCell>
                            <TableCell align="left">
                              {fCurrency(row.price)}
                            </TableCell>
                            <TableCell align="left">
                              {fCurrency(
                                parseFloat(row.price) -
                                  parseFloat(values.cost) *
                                    parseInt(row.quantity, 10),
                              )}{" "}
                              (
                              {(parseFloat(row.price) <= 0
                                ? 0
                                : ((parseFloat(row.price) /
                                    parseInt(row.quantity, 10) -
                                    parseFloat(values.cost)) /
                                    (parseFloat(row.price) /
                                      parseInt(row.quantity, 10))) *
                                  100
                              ).toFixed(2)}
                              %)
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="outlined"
                                size="small"
                                color="error"
                                disabled={!canEdit}
                                onClick={() =>
                                  setFieldValue(
                                    "qtyPrices",
                                    values.qtyPrices.filter(
                                      (item, i) => i !== index,
                                    ),
                                  )
                                }
                              >
                                DELETE
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Stack spacing={2} sx={{ mt: 2 }}>
                    <TextField
                      fullWidth
                      label="Name"
                      disabled={!canEdit}
                      {...getFieldProps("qtyPrice.name")}
                    />
                    <Stack direction={{ md: "row", xs: "column" }} spacing={2}>
                      <TextField
                        fullWidth
                        label="Quantity"
                        type="number"
                        disabled={!canEdit}
                        {...getFieldProps("qtyPrice.quantity")}
                        helperText={
                          values.qtyPrice.name &&
                          `This is the total quantity of ${values.name} in a ${values.qtyPrice.name}.`
                        }
                      />
                      <TextField
                        fullWidth
                        label="Price"
                        disabled={!canEdit}
                        {...getFieldProps("qtyPrice.price")}
                        helperText={
                          values.qtyPrice.name &&
                          `This is the total price for 1 ${values.qtyPrice.name} of ${values.name}.`
                        }
                        InputProps={{
                          inputComponent: NumberFormatCustom,
                        }}
                      />
                    </Stack>
                    <Button
                      variant="contained"
                      color="secondary"
                      disabled={!canEdit}
                      onClick={() => {
                        if (
                          values.qtyPrice.name &&
                          values.qtyPrice.quantity &&
                          parseInt(values.qtyPrice.quantity, 10) > 0 &&
                          parseFloat(values.qtyPrice.price) > 0
                        ) {
                          setFieldValue("qtyPrices", [
                            ...values.qtyPrices,
                            values.qtyPrice,
                          ]);
                          setFieldValue("qtyPrice", {
                            name: "",
                            quantity: "",
                            price: "",
                          });
                        } else {
                          enqueueSnackbar(
                            "Please provide the name, quantity and price to proceed.",
                            {
                              variant: "warning",
                            },
                          );
                        }
                      }}
                    >
                      Add Price
                    </Button>
                  </Stack>
                </Stack>
              )}
            </Stack>
            {canEdit && (
              <LoadingButton
                fullWidth
                size="large"
                type="submit"
                variant="contained"
                loading={isSubmitting}
                sx={{ mt: 5 }}
              >
                Update
              </LoadingButton>
            )}
          </Form>
        </FormikProvider>
      </Box>
    </Card>
  );
}
